/**
 * JWT Helper Utilities
 * ═══════════════════════════════════════════════════════════════════════════
 * Lightweight JWT token decoder for extracting user identity from tokens
 *
 * NOTE: This is CLIENT-SIDE decoding only (not verification)
 * - Token signature verification happens on backend
 * - This just extracts claims for UI/API usage
 *
 * Created: 2026-04-27
 */

import { SecureStorage } from './SecureStorage';
interface JWTPayload {
    sub: string; // PFUser_UUID
    username?: string;
    exp?: number; // Expiration timestamp
    iat?: number; // Issued at timestamp
    type?: string; // 'access' or 'refresh'
}

/**
 * Decode JWT token and extract payload
 * @param token - JWT token string
 * @returns Decoded payload or null if invalid
 */
export function decodeJWT(token: string): JWTPayload | null {
    try {
        // JWT format: header.payload.signature
        const parts = token.split('.');
        if (parts.length !== 3) {
            console.error('[jwtHelper] Invalid JWT format');
            return null;
        }

        // Decode base64 payload (part 1)
        const payload = parts[1];
        const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decoded);
    } catch (error) {
        console.error('[jwtHelper] Failed to decode JWT:', error);
        return null;
    }
}

/**
 * Extract PFUser UUID from access token
 * @param token - JWT access token (optional, reads from localStorage if not provided)
 * @returns PFUser UUID or null
 */
export function getPFUserUuidFromToken(token?: string): string | null {
    const accessToken = token || SecureStorage.getAccessToken();
    if (!accessToken) {
        console.warn('[jwtHelper] No access token found');
        return null;
    }

    const payload = decodeJWT(accessToken);
    if (!payload || !payload.sub) {
        console.error('[jwtHelper] JWT payload missing "sub" claim');
        return null;
    }

    return payload.sub;
}

/**
 * Check if JWT token is expired (client-side only, not secure)
 * @param token - JWT token string
 * @returns true if expired
 */
export function isTokenExpired(token: string): boolean {
    const payload = decodeJWT(token);
    if (!payload || !payload.exp) {
        return true;
    }

    // exp is in seconds, Date.now() is in milliseconds
    return Date.now() >= payload.exp * 1000;
}

/**
 * Ensure pfuser_uuid is stored in localStorage
 * Called after successful authentication to sync JWT → localStorage
 */
export function syncPFUserUuidToLocalStorage(): boolean {
    const pfuserUuid = getPFUserUuidFromToken();
    if (!pfuserUuid) {
        console.error('[jwtHelper] Cannot sync pfuser_uuid - token invalid or missing');
        return false;
    }

    const existingUuid = SecureStorage.getPfuserUuid();
    if (existingUuid !== pfuserUuid) {
        console.log('[jwtHelper] Syncing pfuser_uuid to localStorage');
        SecureStorage.setPfuserUuid(pfuserUuid);
    }

    return true;
}

/**
 * Check if JWT token was issued before cypher rotation
 * Used to enforce re-authentication after security key rotation
 *
 * @param cypherRotationDate - ISO date string of when cypher was rotated
 * @param token - JWT token (optional, reads from localStorage if not provided)
 * @returns true if token is older than rotation (requires re-auth)
 */
export function isTokenOlderThanCypherRotation(
    cypherRotationDate: string | Date,
    token?: string,
): boolean {
    const accessToken = token || SecureStorage.getAccessToken();
    if (!accessToken) {
        console.warn('[jwtHelper] No access token - assuming expired');
        return true;
    }

    const payload = decodeJWT(accessToken);
    if (!payload || !payload.iat) {
        console.warn('[jwtHelper] Token missing "iat" claim - assuming expired');
        return true;
    }

    // Convert rotation date to timestamp (seconds)
    const rotationTimestamp = new Date(cypherRotationDate).getTime() / 1000;

    // Compare: token issued before rotation = needs re-auth
    const isOlder = payload.iat < rotationTimestamp;

    if (isOlder) {
        console.warn('[jwtHelper] Token predates cypher rotation - re-authentication required', {
            tokenIssuedAt: new Date(payload.iat * 1000).toISOString(),
            cypherRotatedAt: new Date(rotationTimestamp * 1000).toISOString(),
        });
    }

    return isOlder;
}
